import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { AmountFormatCell, DateFormatCell } from "components/common/formatter";
import { useState, useEffect } from "react";
import { CrossIcon } from "components/tabs/TabTitle";
import { process } from "@progress/kendo-data-query";

const RECORD_TYPE_LABELS = {
    1: "Time",
    2: "Disbursement",
    3: "Expense",
};

//This is combine grid for Bill Disbursement & Time records
const BillGrid = (props) => {
    const {
        timeData = [],
        disbData = [],
        reset,
        updateTimeData,
        updateDisbData,
        changeTimeTotals,
        changeDisbTotals,
        changeExpenseTotals,
        changeBillTimeData,
        changeBillDisbData,
    } = props;

    const [combinedData, setCombinedData] = useState([]);
    const [dataState, setDataState] = useState({
        group: [{ field: "recordTypeSortOrder", dir: "asc" }],
        sort: [{ field: "date", dir: "asc" }],
    });

    const calculateNetAmount = (item) => {
        return item.billedNetAmount === "w/o"
            ? 0
            : typeof item.billedNetAmount === "number"
            ? item.billedNetAmount
            : item.value;
    };

    useEffect(() => {
        const timeRecords = timeData.map((item) => ({
            ...item,
            recordType: "Time",
            recordTypeSortOrder: 1,
            id: `time-${item.timeId || item.fixedFeeItemID || ''}`,
            displayValue: item.value,
            // If billedNetAmount is explicitly "w/o", keep it; if it's a number, use it; otherwise fallback to value
            displayNet: item.billedNetAmount === "w/o" ? "w/o" : calculateNetAmount(item),
            displayVat: item.billVat,
            // Map fee earner - prefer billFeeEarnerRef (from Time records), fallback to feeEarnerRef (from Profit Cost)
            displayFeeEarner: item.billFeeEarnerRef || item.feeEarnerRef,
            displayNominal: item.nominalRef,
        }));

        const disbRecords = disbData.map((item) => ({
            ...item,
            recordType: item.type === "Expense" ? "Expense" : "Disbursement",
            recordTypeSortOrder: item.type === "Expense" ? 3 : 2,
            id: `disb-${item.timeId || item.postingId}`,
            displayValue: item.netAmount,
            displayNet: item.netAmount,
            displayVat: item.vatAmount,
            displayFeeEarner: item.feeEarnerRef,
            displayNominal: item.nominalRef,
        }));

        setCombinedData([...timeRecords, ...disbRecords]);
    }, [timeData, disbData]);

    useEffect(() => {
        setCombinedData([]);
    }, [reset]);

    useEffect(() => {
        let timeAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        if (timeData.length > 0) {
            timeData.forEach((item) => {
                const netAmount = calculateNetAmount(item);
                timeAmount.totalAmount += netAmount || 0;
                timeAmount.totalVat += item.billVat || 0;
            });
        }

        if (changeTimeTotals) {
            changeTimeTotals(timeAmount);
        }

        let finalData = [];
        timeData.forEach((item) => {
            let datatemp;
            const isWriteOff = item.billedNetAmount === "w/o";
            const netAmount = calculateNetAmount(item);
            
            if (item.type !== "ProfitCost") {
                datatemp = {
                    timeId: item.timeId,
                    netAmount: netAmount,
                    vatAmount: item.billVat,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.billFeeEarnerRef,
                    nominalRef: item.nominalRef,
                };
                if (isWriteOff) {
                    datatemp.isWriteOff = true;
                }
            } else if (item.type === "ProfitCost") {
                datatemp = {
                    date: item.date,
                    details: item.details,
                    netAmount: netAmount,
                    vatAmount: item.billVat,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.billFeeEarnerRef,
                    nominalRef: item.nominalRef,
                    type: item.type,
                };
                if (isWriteOff) {
                    datatemp.isWriteOff = true;
                }
            }
            finalData.push(datatemp);
        });
        if (changeBillTimeData) {
            changeBillTimeData(finalData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeData]);

    useEffect(() => {
        let disbAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        let expenseAmount = {
            totalAmount: 0,
            totalVat: 0,
        };

        if (disbData.length > 0) {
            disbData.forEach((item) => {
                if (item.type === "Expense") {
                    expenseAmount.totalAmount += item.netAmount || 0;
                    expenseAmount.totalVat += item.vatAmount || 0;
                } else {
                    disbAmount.totalAmount += item.netAmount || 0;
                    disbAmount.totalVat += item.vatAmount || 0;
                }
            });
        }

        if (changeDisbTotals) {
            changeDisbTotals(disbAmount);
        }

        if (changeExpenseTotals) {
            changeExpenseTotals(expenseAmount);
        }

        let finalData = [];
        disbData.forEach((item) => {
            if (item.type === "Expense") {
                const datatemp = {
                    date: item.date,
                    details: item.details,
                    netAmount: item.netAmount,
                    vatAmount: item.vatAmount,
                    vatCode: item.vatCode,
                    feeEarnerRef: item.feeEarnerRef,
                    nominalRef: item.nominalRef,
                    type: item.type,
                };
                finalData.push(datatemp);
            } else {
                const datatemp = {
                    postingId: item.postingId,
                    netAmount: item.netAmount,
                    vatAmount: item.vatAmount,
                    vatCode: item.vatCode,
                };
                finalData.push(datatemp);
            }
        });
        if (changeBillDisbData) {
            changeBillDisbData(finalData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disbData]);
       

    const MyEditCommandCell = (props) => {
        const item = props.dataItem;

        return (
            <td>
                <CrossIcon
                    onClick={(e) => {
                        if (item.recordType === "Time") {
                            const updatedData = timeData.filter((record) => {
                                // Handle records with timeId
                                if (item.timeId && record.timeId) {
                                    return record.timeId !== item.timeId;
                                }
                                // Handle records with fixedFeeItemID (from Fixed Fee popup)
                                if (item.fixedFeeItemID && record.fixedFeeItemID) {
                                    return record.fixedFeeItemID !== item.fixedFeeItemID;
                                }
                                // If neither match, keep the record
                                return true;
                            });
                            if (updateTimeData) {
                                updateTimeData(updatedData);
                            }
                        } else if (item.recordType === "Disbursement" || item.recordType === "Expense") {
                            const updatedData = disbData.filter(
                                (record) => {
                                    const disbId = record.timeId || record.postingId;
                                    const itemId = item.timeId || item.postingId;
                                    return disbId !== itemId;
                                }
                            );
                            if (updateDisbData) {
                                updateDisbData(updatedData);
                            }
                        }
                    }}
                />
            </td>
        );
    };

    const HeaderCrossCell = () => {
        return (
            <div className="d-flex justify-content-center align-items-center h-100">
                <CrossIcon
                    onClick={(e) => {
                        if (updateTimeData) {
                            updateTimeData([]);
                        }
                        if (updateDisbData) {
                            updateDisbData([]);
                        }
                    }}
                    style={{ cursor: "pointer" }}
                />
            </div>
        );
    };

    const BillNetCell = (props) => {
        const value = props.dataItem.displayNet;
        
        if (value === "w/o") {
            return (
                <td style={{ textAlign: "center" }}>
                    w/o
                </td>
            );
        }
        return (
            <td style={{ textAlign: "center" }}>
                {typeof value === 'number' ? value.toFixed(2) : (value !== null && value !== undefined ? value : '0.00')}
            </td>
        );
    };

    const customRowRender = (row, rowProps) => {
        if (rowProps.rowType === "groupHeader") {
            const groupValue = rowProps.dataItem.value;
            const label = RECORD_TYPE_LABELS[groupValue] ?? groupValue;
            return (
                <tr style={{ backgroundColor: "#f8f8f8" }}>
                    <td
                        colSpan={8}
                        style={{
                            fontWeight: 800,
                            paddingLeft: "20px",
                            fontSize: "14px",
                            textAlign: "left",
                            color: "#000000",
                        }}
                    >
                        {label}
                    </td>
                </tr>
            );
        }
        return row;
    };



    const resultData = process(combinedData, dataState);

    return (
        <Grid
            data={resultData}
            {...dataState}
            onDataStateChange={(e) => setDataState(e.dataState)}
            groupable={{
                enabled: false,
            }}
            sortable={true}
            expandField="expanded"
            rowRender={customRowRender}
            className="h-100 overflow-auto fs-sm grid-minwidth-col3-200"
        >
            <Column
                field="date"
                title="Date"
                editable={false}
                cell={DateFormatCell}
                width="100px"
                className="ps-1"
            />
            <Column
                field="details"
                title="Details"
                editable={false}                
                width="200px"
                className="ps-0"
            />
            <Column
                field="displayFeeEarner"
                title="FE"
                editable={false}
                width="60px"                
            />
            <Column
                field="displayNominal"
                title="Nominal"
                editable={false}
                width="80px"                
            />
            <Column
                field="displayValue"
                title="Value"
                editable={false}
                width="80px"
                cell={AmountFormatCell}
                headerClassName="grid-header-center"                
            />

            <Column
                field="displayNet"
                title="Bill Net"
                editable={false}
                width="80px"
                cell={BillNetCell}
                headerClassName="grid-header-center"
            />

            <Column
                field="displayVat"
                title="Bill Vat"
                editable={false}
                width="80px"
                cell={AmountFormatCell}
                headerClassName="grid-header-center"
            />
            <Column 
                cell={MyEditCommandCell} 
                headerCell={HeaderCrossCell}
                width={60} 
            />
        </Grid>
    );
};

export default BillGrid;

